/* Ingreso — la puerta.
 *
 * El ingreso ocurre AQUÍ, contra `POST /api/sesion`, sin salir del dominio del frontend. Salir a
 * `api.<dominio>/login` para volver obligaría a que `destino_seguro` aceptara URL absolutas, y eso
 * es justo lo que esa función impide a propósito para que el ingreso no sea un trampolín de
 * phishing.
 *
 * Se entra con USUARIO, no con correo: es lo que guarda `auth.db`. Y no hay recuperación de
 * contraseña — la repone un administrador con una clave temporal, que esta misma pantalla obliga a
 * cambiar en cuanto se entra.
 */
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { enviar, ErrorApi, obtener } from '../api/cliente'
import { entero, fecha } from '../api/formato'
import { useSesion } from '../estado/sesion'
import { IconoSi } from '../ui/Iconos'
import './Ingreso.css'

/**
 * Largo mínimo de contraseña. Copia de `LARGO_MINIMO` en `app/auth/passwords.py`.
 *
 * Se repite aquí para no hacerle escribir a alguien una contraseña que el servidor va a rechazar,
 * y se declara con nombre —en vez de un 12 suelto en el texto— para que la copia sea localizable.
 * La verdad sigue siendo la del servidor: esto solo evita un viaje.
 */
const LARGO_MINIMO_CLAVE = 12

/** Las tres cifras del universo, de `GET /api/publico/cifras`. */
type Cifras = { procesos_abiertos?: number; entidades?: number; corte?: string }

/**
 * Lo que Vigía hace, en tres frases. Va en la puerta porque quien llega aquí puede no saber qué es
 * esto —le pasaron una cuenta— y pedir una clave antes de decir para qué es una descortesía.
 */
const PROMESAS = [
  'La afinidad se calcula con tu RUP y tus contratos en SECOP, no con palabras clave',
  'Cuánto ofertar, contra quién compites y en cuántos días paga cada entidad',
  'Análisis del pliego que avisa de los requisitos hechos a la medida de otro',
]

/**
 * El lado de producto de la puerta (pantalla 6a).
 *
 * ⚠ Las cifras salen VIVAS del cubo, no escritas a mano. Un «1.822 procesos» congelado en el HTML
 * envejece en una noche y convierte la primera frase del producto en su primera mentira. Si el
 * endpoint no responde —el cubo puede no estar—, la franja de cifras simplemente NO se pinta: es
 * mejor no decir el tamaño que decirlo mal.
 */
function LadoProducto() {
  const [c, setC] = useState<Cifras | null>(null)
  useEffect(() => {
    obtener<Cifras>('/api/publico/cifras').then(setC).catch(() => setC(null))
  }, [])

  return (
    <div className="ingreso-producto">
      <div className="ingreso-marca">
        <b>Vigía</b>
        <span className="marca-bajada">Contratación pública · Colombia</span>
      </div>
      <h1>Dónde vale la pena competir, antes de gastar cuarenta horas en una propuesta.</h1>
      <p className="ingreso-pitch">
        Lee todos los días los procesos abiertos de SECOP II, los compara con lo que tu empresa ya
        ha ejecutado, y te dice cuáles te sirven, a qué precio se suele ganar y cómo paga cada
        entidad.
      </p>
      <ul className="ingreso-promesas">
        {PROMESAS.map((p) => (
          <li key={p}><IconoSi /><span>{p}</span></li>
        ))}
      </ul>
      {(c?.procesos_abiertos || c?.entidades) && (
        <p className="ingreso-cifras">
          {c.procesos_abiertos ? (
            <span><b className="cifra">{entero(c.procesos_abiertos)}</b> procesos abiertos</span>
          ) : null}
          {c.entidades ? (
            <span><b className="cifra">{entero(c.entidades)}</b> entidades</span>
          ) : null}
          {c.corte ? <span>datos de SECOP II al {fecha(c.corte)}</span> : null}
        </p>
      )}
    </div>
  )
}

export function Ingreso() {
  const { yo, recargar } = useSesion()
  const ubicacion = useLocation()
  const volverA = (ubicacion.state as { volverA?: string } | null)?.volverA ?? '/'

  const [usuario, setUsuario] = useState('')
  const [clave, setClave] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Ya hay sesión: quien llega aquí por el enlace de una ficha vuelve a esa ficha, no a la portada.
  if (yo && !yo.cambiar_clave) return <Navigate to={volverA} replace />
  if (yo?.cambiar_clave) return <CambiarClave />

  async function entrar(e: FormEvent) {
    e.preventDefault()
    setEnviando(true)
    setError(null)
    try {
      await enviar<{ ok: boolean; cambiar_clave: boolean }>('/api/sesion', { usuario, clave })
      // La cookie ya está puesta; `recargar` trae identidad, permisos y testigo, y de paso decide
      // si toca cambiar la clave antes de dejar pasar.
      await recargar()
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No pude iniciar sesión.')
      setEnviando(false)
    }
  }

  return (
    <div className="ingreso">
      <LadoProducto />
      <form className="ingreso-caja" onSubmit={entrar}>
        <h2 className="ingreso-titulo">Entra a tu cuenta</h2>
        <p className="ingreso-bajada">
          Vigía funciona por invitación: si tu organización ya tiene cuenta, pídele el acceso a
          quien la administra.
        </p>

        <label className="campo">
          <span className="rotulo">Usuario</span>
          <input
            name="usuario"
            autoComplete="username"
            autoFocus
            required
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
          />
        </label>

        <label className="campo">
          <span className="rotulo">Contraseña</span>
          <input
            name="clave"
            type="password"
            autoComplete="current-password"
            required
            value={clave}
            onChange={(e) => setClave(e.target.value)}
          />
        </label>

        {/* `role="alert"` para que un lector de pantalla anuncie el fallo sin tener que buscarlo. */}
        {error && (
          <p className="ingreso-error" role="alert">
            {error}
          </p>
        )}

        <button className="boton-primario" type="submit" disabled={enviando}>
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>

        <p className="ingreso-pie">
          ¿Olvidaste la contraseña? Pídele a quien administra Vigía que te genere una temporal: al
          entrar con ella se te pedirá cambiarla.
        </p>
        {/* La nota de privacidad va en la PUERTA, no enterrada en el perfil: lo primero que se
            pide es cargar un RUP, y quien lo carga tiene derecho a saber a dónde va antes. */}
        <p className="ingreso-privacidad">
          Tus datos —RUP, territorios, capacidad, descartes— son tuyos: no se comparten con las
          entidades ni con otras empresas de la plataforma.
        </p>
      </form>
    </div>
  )
}

/** Clave temporal sin cambiar. Hasta que se cambie, el servidor responde 403 a todo lo demás, así
 *  que no tiene sentido dejar ver la aplicación por detrás. */
function CambiarClave() {
  const { yo, recargar } = useSesion()
  const [nueva, setNueva] = useState('')
  const [repetir, setRepetir] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function guardar(e: FormEvent) {
    e.preventDefault()
    setEnviando(true)
    setError(null)
    try {
      await enviar('/api/cuenta/clave', { nueva, repetir, csrf: yo?.csrf ?? '' })
      await recargar()
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No pude cambiar la contraseña.')
      setEnviando(false)
    }
  }

  return (
    <div className="ingreso">
      <form className="ingreso-caja" onSubmit={guardar}>
        <div className="ingreso-marca">
          <b>Vigía</b>
        </div>
        <h1 className="ingreso-titulo">Elige tu contraseña</h1>
        <p className="ingreso-bajada">
          Entraste con una contraseña temporal. Cambiarla también cierra las demás sesiones abiertas
          de tu cuenta.
        </p>

        <label className="campo">
          <span className="rotulo">Nueva contraseña</span>
          <input
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            minLength={LARGO_MINIMO_CLAVE}
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
          />
        </label>

        <label className="campo">
          <span className="rotulo">Repítela</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={repetir}
            onChange={(e) => setRepetir(e.target.value)}
          />
        </label>

        {error && (
          <p className="ingreso-error" role="alert">
            {error}
          </p>
        )}

        <button className="boton-primario" type="submit" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar y entrar'}
        </button>
        {/* ⚠ El mínimo lo decide el SERVIDOR (`LARGO_MINIMO` en `app/auth/passwords.py`), y aquí se
            REPITE para no hacer escribir una contraseña que va a rechazar. Repetir una regla es
            arriesgarse a que las dos digan cosas distintas, así que el `minLength` del campo y este
            texto salen de la misma constante — y si el servidor sube el suyo, esto miente hasta que
            alguien lo cambie. Lo vigila `app/tests/test_auth.py`. */}
        <p className="ingreso-pie">
          Mínimo {LARGO_MINIMO_CLAVE} caracteres. Al cambiarla se cierran tus otras sesiones
          abiertas.
        </p>
      </form>
    </div>
  )
}
